import { trackValidationError, trackValidationWarning } from './index.js'

/**
 * Gathers all vuelidate ($v) errors and tracks when a user makes an error
 *
 * @param {object} errorsObj
 * @param {string} errorType
 * @returns {string}
 */
export const trackVuelidateErrors = (errorsObj, errorType = false) => {
    if (!errorsObj) {
        return false
    }

    Object.entries(errorsObj)
        .filter(([field, _model]) => !field.startsWith('$'))
        .filter(([_field, model]) => model?.$error)
        .map(([field, model]) => {
            const payload = extractPayload(field, model, errorType)

            trackValidationError(payload)
        })
}

/**
 * Gather a specific vuelidate ($v) error and track it as a warning
 *
 * @param {string} field
 * @param {object } model
 * @param {string} errorType
 * @returns {string}
 */
export const trackVuelidateWarning = (field, model, errorType = false) => {
    if (!field || !model) {
        return false
    }

    const payload = extractPayload(field, model, errorType)

    trackValidationWarning(payload)
}

/**
 * Map the field for a generic message that can be filtered on in Mixpanel.
 *
 * @param {object} model
 * @returns {string}
 */
function getMappedMessage(model) {
    const map = {
        required: 'Field is not filled in',
        email: 'Email is invalid',
        url: 'URL is invalid',
        minLength: 'Field has not reached min length',
        maxLength: 'Field has not reached max length',
        minValue: 'Field has not reached min value',
        maxValue: 'Field is above max value',
        numeric: 'Field is not numeric',
        vatValid: 'VAT number is invalid',
    }
    let message = ''

    Object.keys(map).map((key) => {
        if (model[key] === false) {
            message = map[key]
        }
    })

    return message
}

/**
 * Gathers all GQL userErrors and tracks when a user makes an error
 *
 * @param {object} errorsObj
 * @param data
 * @returns {string}
 */
export const trackGQLErrors = (errorsObj, data) => {
    if (!errorsObj) {
        return false
    }

    const validationMessages = {
        cocNumber: 'Registration Number is in use',
        vatNumber: 'VAT Number is in use',
        email: 'This email is already taken / or invalid',
        mobilePhone: 'Mobile Phone is invalid',
    }

    errorsObj.map((err) => {
        const field = err.field[0]
        let value = 'Untracked'
        const displayedMessage = validationMessages?.[field] || field

        if (data?.[field]) {
            value = data?.[field] || ''
        }

        trackValidationError({
            validationReason: displayedMessage,
            validationField: field,
            validationValue: value || '',
            errorType: 'GQL response',
        })
    })
}

/**
 * Convert a Vuelidate model and some meta data into a tracking object.
 *
 * @param {string} field
 * @param {object} model
 * @param {string} errorType
 * @returns {object}
 */
function extractPayload(field, model, errorType = false) {
    return {
        validationReason: getMappedMessage(model) || 'Unknown (not mapped)',
        validationField: field,
        validationValue: model.$model || '',
        params: Object.keys(model.$params),
        errorType,
    }
}
